<template>
  <div
      id="view"
      class="app-body"
      data-testid="ring-group-view"
  >

    <el-dialog
        :visible.sync="show_help"
        append-to-body
        data-testid="help-dialog"
        title="What are inboxes?"
        top="10vh"
    >
      <p class="text-md _600 break-word">Inboxes are a set of Users (Agents & Reps) that receive
                                         and
                                         answer your calls in a predefined manner. Each inbox
                                         has a queue.</p>
      <p class="text-md _600 break-word">Lines route your calls to teams, group of agents, or queues
                                         that you
                                         create here. Queue parameters are defined within each inbox.
                                         </p>
    </el-dialog>

    <div
        :style="{top: (top_offset+50) + 'px'}" class="fixed-header padding pt-0 pb-0"
        data-testid="ring-group-header"
    >
      <div class="row mb-0 mt-0">
        <div class="d-flex flex-fill flex-wrap p-2">
          <div class="d-flex flex-fill flex-xl-grow-0 mr-auto pr-lg-2 search-toolbar">
            <el-input
                ref="ringGroupSearch"
                v-model="search_text"
                autofocus
                clearable
                data-testid="ring-group-search"
                placeholder="Search"
                prefix-icon="el-icon-search"
            >
            </el-input>
          </div>

          <div class="d-flex flex-fill">
            <el-select
              v-model="ring_group_filter"
              placeholder="Select"
              data-testid="ring-group-filter-dropdown"
            >
              <el-option 
                v-for="option in ring_group_filter_options"
                :key="option.value"
                :label="option.label"
                :value="option.value" 
              />
            </el-select>
          </div>

          <div class="d-flex flex-wrap">
            <div
                v-if="showTeamInboxAlertButton"
                class="mt-2 mt-lg-0 mr-2"
            >
              <!-- Desktop version with tooltip -->
              <el-tooltip
                  content="You have agents without any inboxes, click here to view"
                  placement="bottom"
                  class="d-none d-md-block"
              >
                <button
                    class="btn btn-md rounded btn-warning text-white"
                    data-testid="team-inbox-alert-btn"
                    @click="showTeamInboxAlert"
                >
                  <i class="fa fa-exclamation-triangle mr-1"></i>
                  <span class="_600">{{ usersWithoutAccessCount }} {{ usersWithoutAccessCount === 1 ? 'User' : 'Users' }} Without any Inbox</span>
                </button>
              </el-tooltip>

              <!-- Mobile version without tooltip -->
              <button
                  class="btn btn-md rounded btn-warning text-white d-md-none"
                  data-testid="team-inbox-alert-btn-mobile"
                  @click="showTeamInboxAlert"
              >
                <i class="fa fa-exclamation-triangle mr-1"></i>
                <span class="_600">{{ usersWithoutAccessCount }} {{ usersWithoutAccessCount === 1 ? 'User' : 'Users' }} Without Inbox</span>
              </button>
            </div>

            <div class="mt-2 mt-lg-0 mr-2">
                <button
                  class="btn btn-md rounded purplish"
                  data-testid="bulk-manage-ring-groups-btn"
                  @click="bulkAddUsers"
                >
                    <i class="fa fa-cogs mr-1"></i>
                    Bulk Add Users
                </button>
            </div>

            <div
                v-if="!hasRole('Company Agent') && hasPermissionTo('create ring group')"
                class="mt-2 mt-lg-0 mr-2"
            >
              <button
                  class="btn btn-md rounded greenish"
                  data-testid="add-ring-group-btn"
                  @click="addRingGroup"
              >
                <i class="fa fa-plus"></i>
                Add Inbox
              </button>
            </div>

            <div
                v-if="isTrial"
                class="mt-3 mt-lg-0 mr-2"
            >
              <video-modal-activator data-testid="video-modal-activator" />
            </div>

            <div
                v-else
                class="mt-2 mt-lg-0 mr-2 mr-lg-0 d-flex flex-column justify-content-center text-blue"
            >
              <i
                  class="material-icons text-lg cursor-pointer"
                  data-testid="help-info-icon"
                  @click="toggleHelp"
              >
                info_outline
              </i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
        v-if="hasPermissionTo('list report') && !search_text"
        class="padding mt-4 pt-3"
        data-testid="ring-group-reports"
    >
      <div class="row box">
        <div class="col-12">
          <div class="no-effect">
            <div class="box-header pb-0">
              <activity-graph
                  :default_date_range="7"
                  :is_first_load="reports_first_load"
                  base="ring_group"
                  data-testid="ring-group-activity-graph"
              >
              </activity-graph>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="padding">
      <div v-if="hasPermissionTo('list ring group')">
        <div class="row box">
          <div class="col-12">
            <div class="no-effect">
              <div class="box-header">
                <div class="row">
                  <div class="col-12">
                                        <span
                                            class="pull-left font-120"
                                            data-testid="ring-group-count"
                                        >
                                            <strong>{{ dataCount || 0 }}</strong>
                                            {{ dataCount > 1 ? 'inboxes' : 'inbox' }}
                                        </span>
                  </div>
                </div>
              </div>
              <div class="box-body">
                <div v-on:mouseleave="tableLeave">
                  <el-table
                      v-loading="loading"
                      :data="paginatedData"
                      :default-sort="{prop: 'name', order: 'ascending'}"
                      class="w-full"
                      data-testid="ring-groups-table"
                      fit
                      stripe
                      @cell-mouse-enter="cellEnter"
                  >
                    <el-table-column
                          data-testid="ring-group-icon-column"
                          label=""
                          width="60"
                    >
                      <template v-slot="scope">
                        <ring-group-icon
                            :type="scope.row.is_personal_inbox ? 'personal' : 'team'"
                        />
                      </template>
                    </el-table-column>
                    <el-table-column
                        data-testid="ring-group-id-column"
                        label="ID"
                        prop="id"
                        sortable
                        width="100"
                    >
                      <template v-slot="scope">
                        <span class="text-greyish">{{ scope.row.id }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column
                        data-testid="ring-group-name-column"
                        label="Name"
                        prop="name"
                        sortable
                        width="300"
                    >
                      <template v-slot="scope">
                        <router-link
                            :data-testid="'ring-group-link-' + scope.row.id"
                            :to="{ name: 'Ring Group Dialog', params: {ring_group_id: scope.row.id }}"
                        >
                                                    <span
                                                        :title="scope.row.name"
                                                        class="text-dark-greenish text-ellipsis"
                                                    >
                                                        {{ scope.row.name }}
                                                    </span>
                        </router-link>
                      </template>
                    </el-table-column>
                    <el-table-column
                        v-if="hasPermissionTo('list campaign')"
                        :min-width="200"
                        data-testid="ring-group-lines-column"
                        label="Connected Lines"
                    >
                      <template v-slot="scope">
                        <template v-if="scope.row.campaign_ids.length">
                          <ul>
                            <li
                                v-for="(campaign_id, index) in scope.row.campaign_ids"
                                v-if="(!showAllLines(scope.row.id) && index < 5) || showAllLines(scope.row.id)"
                                :key="index"
                                :data-testid="'ring-group-line-' + scope.row.id + '-' + campaign_id"
                            >
                              <span class="text-greyish">{{
                                  campaign_id | campaignName(campaigns)
                                                         }}</span>
                            </li>
                            <template v-if="scope.row.campaign_ids.length > 5">
                              <li
                                  v-if="showAllLines(scope.row.id)"
                                  style="list-style-type: none"
                              >
                                <a
                                    :data-testid="'ring-group-show-less-' + scope.row.id"
                                    class="text-blue"
                                    @click.prevent="seeMoreLinesToggle(scope.row.id, false)"
                                >Show less</a>
                              </li>
                              <li
                                  v-if="!showAllLines(scope.row.id)"
                                  style="list-style-type: none"
                              >
                                <a
                                    :data-testid="'ring-group-show-more-' + scope.row.id"
                                    class="text-blue"
                                    @click.prevent="seeMoreLinesToggle(scope.row.id)"
                                >and {{ scope.row.campaign_ids.length - 5 }} more, click to
                                 expand</a>
                              </li>
                            </template>
                          </ul>
                        </template>
                        <span v-else class="text-greyish">-</span>
                      </template>
                    </el-table-column>
                    <el-table-column
                        v-if="isAdmin"
                        align="center"
                        data-testid="ring-group-team-column"
                        label="Team"
                        prop="teams"
                        sortable
                        width="150"
                    >
                      <template v-slot="scope">
                        <template v-if="scope.row.teams && scope.row.teams.length">
                                                    <span
                                                        v-if="scope.row.teams.length > 1"
                                                        :data-testid="'ring-group-team-count-' + scope.row.id"
                                                        class="text-dark-greenish"
                                                    >
                                                        {{ scope.row.teams.length }} Teams
                                                    </span>
                          <span
                              v-else
                              :data-testid="'ring-group-team-name-' + scope.row.id"
                              class="text-dark-greenish"
                          >
                                                        {{ scope.row.teams[0].name }}
                                                    </span>
                        </template>
                        <span
                            v-else
                            :data-testid="'ring-group-team-empty-' + scope.row.id"
                            class="text-dark-greenish"
                        >
                                                    -
                                                </span>
                      </template>
                    </el-table-column>
                    <el-table-column
                        align="center"
                        data-testid="ring-group-users-column"
                        label="Users"
                        prop="user_ids"
                        sortable
                        width="150"
                    >
                      <template v-slot="scope">
                        <template v-if="scope.row.user_ids">
                                                    <span
                                                        v-if="scope.row.user_ids.length > 1"
                                                        :data-testid="'ring-group-users-count-' + scope.row.id"
                                                        class="text-dark-greenish"
                                                    >
                                                        {{ scope.row.user_ids.length }} Users
                                                    </span>
                          <template
                              v-else-if="scope.row.user_ids.length == 1 && getUser(scope.row.user_ids[0])"
                          >
                                                        <span
                                                            v-if="getUser(scope.row.user_ids[0])"
                                                            :data-testid="'ring-group-user-name-' + scope.row.id"
                                                            :title="getUserName(getUser(scope.row.user_ids[0]))"
                                                            class="text-dark-greenish"
                                                        >
                                                            {{
                                                            getUserName(getUser(scope.row.user_ids[0]))
                                                            }}
                                                        </span>
                          </template>
                          <span
                              v-else
                              :data-testid="'ring-group-users-empty-' + scope.row.id"
                              class="text-dark-greenish"
                          >
                                                        -
                                                    </span>
                        </template>
                      </template>
                    </el-table-column>
                    <el-table-column
                        align="center"
                        data-testid="ring-group-active-users-column"
                        label="Active Users"
                        sortable
                        width="200"
                    >
                      <template v-slot="scope">
                                                <span
                                                    v-if="scope.row.user_ids"
                                                    :class="[getActiveUsers(scope.row?.user_ids, scope.row?.team_ids) == 0 ? 'text-danger' : 'text-greyish']"
                                                    class="text-ellipsis"
                                                >
                                                    {{
                                                    getActiveUsers(scope.row?.user_ids, scope.row?.team_ids)
                                                    }}
                                                </span>
                        <span
                            v-else
                            class="text-muted"
                        >
                                                    -
                                                </span>
                      </template>
                    </el-table-column>
                    <el-table-column
                        align="center"
                        data-testid="ring-group-available-users-column"
                        label="Available Users"
                        sortable
                        width="200"
                    >
                      <template v-slot="scope">
                                                <span
                                                    v-if="scope.row.user_ids"
                                                    :class="[getAvailableUsers(scope.row?.user_ids, scope.row?.team_ids) == 0 ? 'text-danger' : 'text-greyish']"
                                                    class="text-ellipsis"
                                                >
                                                    {{
                                                    getAvailableUsers(scope.row?.user_ids, scope.row?.team_ids)
                                                    }}
                                                </span>
                        <span
                            v-else
                            class="text-muted"
                        >
                                                    -
                                                </span>
                      </template>
                    </el-table-column>
                    <el-table-column
                        data-testid="ring-group-dial-mode-column"
                        label="Dial Mode"
                        prop="dial_mode"
                        sortable
                        width="150"
                    >
                      <template v-slot="scope">
                                                <span class="text-greyish text-ellipsis">
                                                    {{
                                                    scope.row.dial_mode | humanReadableDialMode
                                                    }}
                                                </span>
                      </template>
                    </el-table-column>
                    <el-table-column
                        v-if="!hasRole('Company Agent')"
                        align="center"
                        data-testid="ring-group-operations-column"
                        fixed="right"
                        label="Operations"
                        width="180"
                    >
                      <template v-slot="scope">
                        <div
                            slot="reference"
                            :data-testid="'ring-group-operations-' + scope.row.id"
                            v-bind:style="getStyle(scope.row.id)"
                        >
                          <div
                              v-if="hasPermissionTo(['view ring group', 'list communication', 'view communication'])"
                              class="row"
                          >
                            <div class="col-12">
                              <router-link
                                  :data-testid="'ring-group-activity-link-' + scope.row.id"
                                  :to="{ name: 'Ring Group Activity', params: {ring_group_id: scope.row.id }}"
                              >
                                <button class="btn btn-sm btn-block purplish">
                                  <i class="fa fa-signal pull-left"></i>
                                  Activity
                                </button>
                              </router-link>
                            </div>
                          </div>

                          <div
                              v-if="hasPermissionTo('update ring group') || hasPermissionTo('delete ring group')"
                              class="row mt-2"
                          >
                            <div class="col-12">
                              <router-link
                                  :data-testid="'ring-group-settings-link-' + scope.row.id"
                                  :to="{ name: 'Ring Group Dialog', params: { ring_group_id: scope.row.id }}"
                              >
                                <button class="btn btn-sm btn-block info">
                                  <i class="fa fa-cogs pull-left"></i>
                                  Settings
                                </button>
                              </router-link>
                            </div>
                          </div>

                          <div
                              v-if="hasPermissionTo('delete ring group') && canDeleteRingGroup(scope.row)"
                              class="row mt-2"
                          >
                            <div class="col-12">
                              <button
                                  :data-testid="'ring-group-delete-btn-' + scope.row.id"
                                  class="btn btn-sm btn-block danger"
                                  @click="deleteRingGroupRemote(scope.row.id)"
                              >
                                <i class="fa fa-trash pull-left"></i>
                                Delete
                              </button>
                            </div>
                          </div>

                          <div class="row mt-2">
                            <div class="col-12">
                              <button
                                  v-if="hasInboxPermission(scope.row)"
                                  :data-testid="'ring-group-inbox-btn-' + scope.row.id"
                                  class="btn btn-sm btn-block bluish"
                                  @click="openInbox(scope.row.id)"
                              >
                                <i class="fa fa-inbox pull-left"></i>
                                Go to Inbox
                              </button>
                            </div>
                          </div>
                        </div>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
                <div
                    v-if="pageCount >= 1"
                    class="mt-3"
                    data-testid="ring-group-pagination"
                >
                  <el-pagination
                      :current-page.sync="pagination.current_page"
                      :page-size="20"
                      :page-sizes="[10, 20, 50, 100, 250]"
                      :total="dataCount"
                      background
                      class="pull-right"
                      data-testid="ring-group-pagination-control"
                      layout="sizes, prev, pager, next"
                      @current-change="changePage"
                      @size-change="handleSizeChange"
                  >
                  </el-pagination>
                  <div class="clearfix"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-backtop
        ref="backtop"
        :bottom="20"
        :right="20"
        data-testid="ring-group-backtop"
        target="html"
    >
    </el-backtop>

    <video-modal
        v-if="isTrial"
        :has-activator-button="true"
        class="pl-2"
        cookie-name="ring-group"
        data-testid="ring-group-video-modal"
        learn-more-link="https://support.aloware.com/en/articles/9031514-mapping-ring-groups-for-inbound-calls-in-aloware"
        notes="🔥 Dive into Inboxes! A select squad of Agents & Reps awaiting your calls with precision. </br></br>Each inbox, its own unique queue. With Lines, steer those calls to dream teams, agent clusters, or the perfect queue. </br></br>Customize that queue magic within each inbox! 💥📞🔥"
        title="🔥 Dive into Inboxes!"
        video-url="https://www.youtube.com/embed/4_Y5MAUjh3k?si=xPantGBLf_VQC9T-"
    />

    <ring-group-bulk-manage-modal
      :visible.sync="showBulkAddUsersModal"
      @close="onCloseBulkAddUsersModal"
    />
  </div>
</template>

<script>
import auth from '../../auth'
import {
  acl_mixin,
  chargebee_mixin,
  paginator_mixin,
  scroll_mixin,
  styling_mixin,
  kyc_mixin,
  teams_mixin
} from '../../mixins'
import {mapActions, mapState, mapGetters} from 'vuex'
import * as AnswerTypes from '../../constants/answer-types'
import * as AgentStatus from '../../constants/agent-status'
import VideoModal from '../../components/video-modal.vue'
import VideoModalActivator from '../../components/video-modal-activator.vue'
import Alert from '../../components/alert/index.vue'
import {TEAMINBOX_NAME_PLURAL, TEAMINBOX_PATH_NAME} from '../../constants/teaminbox'
import RingGroupIcon from '../../components/ring-groups/icons/ring-group-icon.vue'
import RingGroupBulkManageModal from '../../components/ring-groups/ring-group-bulk-manage-modal.vue'

export default {
  components: {
    VideoModal,
    VideoModalActivator,
    'al-alert': Alert,
    RingGroupIcon,
    RingGroupBulkManageModal
  },

  mixins: [
    styling_mixin,
    chargebee_mixin,
    paginator_mixin,
    scroll_mixin,
    acl_mixin,
    kyc_mixin,
    teams_mixin
  ],

  data() {
    return {
      auth: auth,
      loading: false,
      loading_btn: false,
      loading_clear_new_leads: false,
      search_text: '',
      search_fields: ['id', 'name', 'description'],
      visible_row_id: null,
      editingRingGroup: null,
      dataKey: 'visibleRingGroups',
      dataSort: 'name',
      dataSortType: 'ASC',
      show_help: false,
      reports_first_load: true,
      AgentStatus,
      AnswerTypes,
      see_more_lines: [],
      TEAMINBOX_NAME_PLURAL,
      TEAMINBOX_PATH_NAME,
      showBulkAddUsersModal: false,
      ring_group_filter: 'all',
      ring_group_filter_options: [
        { label: 'All Inboxes', value: 'all' },
        { label: 'Personal Inboxes', value: 'personal' },
        { label: 'Connected Inboxes', value: 'team' }
      ]
    }
  },

  computed: {
    ...mapState(['campaigns', 'ring_groups', 'users']),
    ...mapState('cache', ['current_company', 'show_graph']),
    ...mapGetters('cache', ['isTrial']),
    ...mapGetters('teamInbox', [
      'hasUsersWithoutTeamInboxAccess',
      'usersWithoutTeamInboxAccessCount'
    ]),

    visibleRingGroups() {
      if (this.ring_groups) {
        // exclude call waiting ring groups
        let ring_groups = this.ring_groups

        // Apply ring group filter first
        if (this.ring_group_filter !== 'all') {
          ring_groups = ring_groups.filter(ring_group => {
            return ring_group.is_personal_inbox === (this.ring_group_filter === 'personal')
          })
        }

        // Then apply search filter
        if (this.search_text !== '') {
          let filtered = []
          for (let ring_group of ring_groups) {
            let keys = this.search_fields

            for (let key of keys) {
              if (key.indexOf('.') !== -1) {
                let mixed = key.split(".")
                if (ring_group[mixed[0]]) {
                  if (ring_group[mixed[0]][mixed[1]].toString().toLowerCase().indexOf(this.search_text.toString().toLowerCase()) !== -1) {
                    filtered.push(ring_group)
                    break
                  }
                }
              } else if (ring_group[key]) {
                if (ring_group[key].toString().toLowerCase().indexOf(this.search_text.toString().toLowerCase()) !== -1) {
                  filtered.push(ring_group)
                  break
                }
              }
            }
          }
          return filtered
        }

        return ring_groups
      }

      return []
    },

    showTeamInboxAlertButton() {
      return this.hasUsersWithoutTeamInboxAccess &&
             this.hasRole('Company Admin');
    },

    usersWithoutAccessCount() {
      return this.usersWithoutTeamInboxAccessCount;
    }
  },

  mounted() {
    this.setTitle()

    if (!this.hasPermissionTo('list ring group')) {
      this.goBack()
    }

    // focus ring group search input on page visit
    if (this.$refs.ringGroupSearch) {
      this.$refs.ringGroupSearch.focus()
    }

    // Refresh users without team inbox access when page loads
    if (this.hasRole('Company Admin')) {
      this.$store.dispatch('teamInbox/checkUsersWithoutTeamInboxAccess')
    }

    this.initializeUrlFilter()
  },

  methods: {
    seeMoreLinesToggle(ring_group_id, see_more = true) {
      if (see_more) {
        if (!this.see_more_lines.includes(ring_group_id)) {
          this.see_more_lines.push(ring_group_id)
        }
      }

      if (!see_more) {
        let item_index = this.see_more_lines.indexOf(ring_group_id)

        if (item_index > -1) {
          this.see_more_lines.splice(item_index, 1)
        }
      }
    },

    showAllLines(ring_group_id) {
      return this.see_more_lines.includes(ring_group_id);
    },

    setTitle() {
      axios.get('/get-statics')
          .then(res => {
            let name = res.data.name
            this.setPageTitle('Inboxes - ' + name)
          })
          .catch(err => {
            this.setPageTitle('Inboxes - Aloware')
            console.log(err)
            this.$root.handleErrors(err.response)
          })
    },

    cellEnter: _.throttle(function (row, column, cell, event) {
      this.visible_row_id = row.id
    }, 50),

    tableLeave() {
      this.visible_row_id = null
    },

    getStyle(id) {
      let opacity = 0.5
      if (id == this.visible_row_id) {
        opacity = 1
      }

      return {
        opacity: opacity
      }
    },

    getAvailableUsers(user_ids, team_ids) {
      let available_count = 0

      const unique_users_ids = this.getUniqueUserIds(user_ids || [], team_ids || [])

      for (let i = 0; i < unique_users_ids.length; i++) {
        let user = this.getUser(unique_users_ids[i])

        if (this.isUserAvailable(user)) {
          available_count++
        }
      }

      return available_count
    },

    isUserAvailable(user) {
      //TODO: move this to a constant file after verify difference between user and agent statuses
      const USER_STATUS_ONLINE = 'online'

      if (!user || !user.active) {
        return false
      }

      // browser based agents
      if (user.answer_by == AnswerTypes.BY_BROWSER && user.status == USER_STATUS_ONLINE && user.agent_status == AgentStatus.AGENT_STATUS_ACCEPTING_CALLS) {
        return true
      }

      // answer by phone number or ip phone agents
      if ([AnswerTypes.BY_PHONE_NUMBER, AnswerTypes.BY_IP_PHONE].includes(user.answer_by)) {
        return true
      }

      // normal users
      if (user.agent_status == AgentStatus.AGENT_STATUS_ACCEPTING_CALLS) {
        return true
      }

      return false
    },

    getActiveUsers(user_ids, team_ids) {
      let active_count = 0

      const unique_users_ids = this.getUniqueUserIds(user_ids || [], team_ids || [])

      for (let i = 0; i < unique_users_ids.length; i++) {
        let user = this.getUser(unique_users_ids[i])
        // user active check
        if (user && user.active == true) {
          active_count++
        }
      }

      return active_count
    },

    getUniqueUserIds(user_ids = [], team_ids = []) {
      let uniqueUserIds = []

      // Add user IDs from user_ids array
      if (user_ids && user_ids.length) {
        uniqueUserIds.push(...user_ids)
      }
      // Add user IDs from each team in team_ids array
      if (team_ids && team_ids.length) {
        team_ids.forEach(team_id => {
          let foundTeam = this.all_teams.find(team => team.id === team_id)
          if (foundTeam && foundTeam.users) {
            foundTeam.users.forEach(({id}) => {
              uniqueUserIds.push(id)
            })
          }
        })
      }

      // Remove duplicates by converting to a Set and back to an array
      uniqueUserIds = [...new Set(uniqueUserIds)]

      return uniqueUserIds
    },

    getUser(id) {
      if (!id) {
        return null
      }
      let found = this.users.find(user => user.id === id)
      if (found) {
        return found
      }

      return null
    },

    getUserName(user) {
      if (!user) {
        return
      }

      if (user.answer_by !== undefined) {
        switch (user.answer_by) {
          case AnswerTypes.BY_BROWSER:
            return user.name + ' - Browser / Apps'
          case AnswerTypes.BY_IP_PHONE:
            return user.name + ' - SIP (IP Phone)'
          case AnswerTypes.BY_PHONE_NUMBER:
            return user.name + ' - Phone Number (' + user.phone_number + ')'
          case AnswerTypes.BY_NONE:
            return user.name + ' - Will Not Answer'
        }
      }
      if (user.name !== '' && user.name && !user.sip_uri) {
        return user.name + ' (' + user.phone_number + ')'
      } else if (user.name !== '' && user.name && user.sip_uri) {
        return user.name + ' - SIP'
      } else if (!user.name && user.sip_uri) {
        return 'No Name - SIP'
      } else {
        return 'No Name (' + user.phone_number + ')'
      }
    },

    addRingGroup() {
      this.$router.push({name: 'Ring Group Dialog'})
    },

    canDeleteRingGroup(ringGroup) {
      if (!ringGroup.is_personal_inbox) {
        return true
      }

      return !ringGroup.campaign_ids?.length
        && !ringGroup.user_ids?.length
        && !ringGroup.team_ids?.length
        && !ringGroup.watcher_user_ids?.length
        && !ringGroup.watcher_team_ids?.length
    },

    deleteRingGroupRemote(ring_group_id) {
      if (this.ring_groups) {
        let ring_group = this.ring_groups.find(ring_group => ring_group.id === ring_group_id)
        let confirmMessage = 'Deleting an inbox will remove it from all lines and this could be a breaking change. Continue?'
        let confirmTitle = 'Warning'
        let confirmButtonText = 'OK'

        if (ring_group && ring_group.is_personal_inbox) {
          confirmMessage = 'This action can\'t be undone.'
          confirmTitle = 'Are you sure you want to delete this Inbox?'
          confirmButtonText = 'Delete Inbox'
        }

        this.$confirm(confirmMessage, confirmTitle, {
          confirmButtonText: confirmButtonText,
          cancelButtonText: 'Cancel',
          confirmButtonClass: ring_group && ring_group.is_personal_inbox ? 'bg-red border-danger' : '',
          type: 'warning',
          customClass: 'large-box width-500 fixed'
        }).then(() => {
          this.loading = true
          axios.delete('/api/v1/ring-group/' + ring_group_id)
              .then(res => {
                this.deleteRingGroup(ring_group)
                this.loading = false
              })
              .catch(err => {
                this.$root.handleErrors(err.response)
                this.loading = false
              })
        }).catch(() => {

        })
      }
    },

    cancelEdit() {
      this.editingRingGroup = null
    },

    toggleHelp() {
      this.show_help = !this.show_help
    },

    openInbox(id) {
      let talk_url = this.getTalkUrl()

      if (talk_url) {
        talk_url += `/${TEAMINBOX_PATH_NAME}/${id}`
        window.open(talk_url)
      }
    },

    hasInboxPermission(ring_group) {
      if (!this.auth?.user?.profile?.id) {
        return false
      }

      return ring_group.user_ids?.includes(this.auth.user.profile.id) // user connected
          || ring_group.watcher_user_ids?.includes(this.auth.user.profile.id) // user watching
          || ring_group.team_ids?.some(team_id => this.teamsIds.includes(team_id)) // team connected
          || ring_group.watcher_team_ids?.some(team_id => this.teamsIds.includes(team_id)) // team watching
    },

    showTeamInboxAlert() {
      this.showTeamInboxAlertAction();
    },

    initializeUrlFilter() {
      const type = this.$route.query.type;
      if (type && this.ring_group_filter_options.some(option => option.value === type)) {
        this.ring_group_filter = type;
      }
    },

    bulkAddUsers() {
      this.showBulkAddUsersModal = true
    },

    onCloseBulkAddUsersModal() {
      this.showBulkAddUsersModal = false
    },

    ...mapActions(['deleteRingGroup']),
    ...mapActions('teamInbox', {
      showTeamInboxAlertAction: 'showTeamInboxAlert'
    })
  },

  watch: {
    'show_graph': function () {
      this.reports_first_load = false
    },

    ring_group_filter () {
      // Update URL query parameters when filter changes
      this.$router.push({
        query: {
          ...this.$route.query,
          type: this.ring_group_filter
        }
      }).catch(() => {
        // Ignore navigation errors (e.g., when navigating to same route)
      })
    }
  },

  beforeRouteEnter(to, from, next) {
    auth.check()
        .then((res) => {
          if (res.data.user.is_reseller) {
            // redirect to account management portal if the company is a reseller
            next({name: 'Account Management Portal'})
          } else {
            next()
          }
        })
        .catch((err) => {
          next({name: 'Login', query: {redirect: to.fullPath}})
        })
  }
}
</script>
